// Shared time-window pagination for the mints/burns/swaps transaction feed.
//
// Fetching a fixed count per type (old approach) breaks down because swaps are
// orders of magnitude denser than mints/burns: a fixed "top 75 swaps" batch might
// cover a few hours, while "top 50 mints" can span the token's entire history.
// Merging those two independently-truncated streams produces a cliff - dense
// recent items followed by a jump to whatever sparse mint/burn happened to be
// oldest in that batch.
//
// Instead, all three types are fetched over the SAME time window. If a window
// comes back too thin, it doubles and retries, so pagination always reflects
// real chronological density instead of a per-type truncation artifact.
//
// Pagination walks in one of two directions:
// - 'desc' (default): windows step backward from an end anchor toward a lower bound
//   (0 for "all history", or an explicit `from` for a custom date range).
// - 'asc': windows step forward from a start anchor toward an upper bound
//   (roughly "now" for "all history", or an explicit `to` for a custom date range).
// Both walk the same doubling-window logic below - they just disagree on which
// side of the window is fixed and which side advances.

export const INITIAL_WINDOW_SECONDS = 60 * 60 * 4 // start with 4 hours
export const MAX_WINDOW_SECONDS = 60 * 60 * 24 * 400 // ~400 days: stop expanding past this
export const MIN_ITEMS_PER_BATCH = 10 // expand the window if fewer than this many items (combined) come back
export const SAFETY_CAP_PER_TYPE = 500 // hard cap per type per fetch, in case a single window is very dense
// x6 instead of x2: fewer sequential round-trips to reach a non-thin window on quiet
// pairs, which is what was making the list visibly stall for several seconds on first load.
export const WINDOW_EXPANSION_FACTOR = 6

export function initialWindowEnd() {
  return Math.floor(Date.now() / 1000) + 60
}

export function countItems(batch) {
  return (batch?.mints?.length ?? 0) + (batch?.burns?.length ?? 0) + (batch?.swaps?.length ?? 0)
}

export function shouldExpandWindow(itemCount, windowSeconds) {
  return itemCount < MIN_ITEMS_PER_BATCH && windowSeconds < MAX_WINDOW_SECONDS
}

export function nextWindowSeconds(windowSeconds) {
  return Math.min(windowSeconds * WINDOW_EXPANSION_FACTOR, MAX_WINDOW_SECONDS)
}

// The [windowStart, windowEnd) to fetch next, given how far pagination has gotten
// (`anchor`: the fixed edge reached so far) and where it's ultimately headed (`boundary`).
// 'desc' fixes windowEnd at anchor and grows windowStart backward toward boundary.
// 'asc' fixes windowStart at anchor and grows windowEnd forward toward boundary.
export function nextWindow(direction, anchor, boundary, windowSeconds) {
  if (direction === 'asc') {
    return { windowStart: anchor, windowEnd: Math.min(anchor + windowSeconds, boundary) }
  }
  return { windowStart: Math.max(anchor - windowSeconds, boundary), windowEnd: anchor }
}

// Whether the just-fetched window is thin AND there's still room to keep expanding
// before hitting the boundary - i.e. whether to widen the window and refetch.
export function canExpand(direction, windowStart, windowEnd, boundary, itemCount, windowSeconds) {
  const roomLeft = direction === 'asc' ? windowEnd < boundary : windowStart > boundary
  return roomLeft && shouldExpandWindow(itemCount, windowSeconds)
}

// Merge a freshly-fetched window's batch into the accumulated feed state.
// feedState is null on the very first call. `cursor` is the new fixed edge reached
// (windowStart for 'desc', windowEnd for 'asc'), clamped to the boundary.
export function mergeTransactionBatch(feedState, batch, cursor, boundary = 0, direction = 'desc') {
  const clampedCursor = direction === 'asc' ? Math.min(cursor, boundary) : Math.max(cursor, boundary)
  const hasMore = direction === 'asc' ? clampedCursor < boundary : clampedCursor > boundary

  return {
    mints: [...(feedState?.mints ?? []), ...(batch?.mints ?? [])],
    burns: [...(feedState?.burns ?? []), ...(batch?.burns ?? [])],
    swaps: [...(feedState?.swaps ?? []), ...(batch?.swaps ?? [])],
    cursor: clampedCursor,
    boundary,
    direction,
    hasMore,
  }
}
